import { ReactNode, useId, useState } from 'react';
import { ApiError } from '../api/client';

export function Campo(props: {
  etiqueta: string;
  ayuda?: ReactNode;
  error?: string | null;
  opcional?: boolean;
  children: (id: string) => ReactNode;
  contador?: { actual: number; max: number };
}) {
  const id = useId();
  return (
    <div className={'campo' + (props.error ? ' campo--error' : '')}>
      <label htmlFor={id}>
        {props.etiqueta}
        {props.opcional && <span className="campo__opcional"> (opcional)</span>}
      </label>
      {props.children(id)}
      <div className="campo__pie">
        {props.error ? (
          <span className="campo__msg-error" role="alert">{props.error}</span>
        ) : (
          props.ayuda && <span className="campo__ayuda">{props.ayuda}</span>
        )}
        {props.contador && (
          <span className={'campo__contador' + (props.contador.actual > props.contador.max ? ' excedido' : '')}>
            {props.contador.actual}/{props.contador.max}
          </span>
        )}
      </div>
    </div>
  );
}

export function Boton(props: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'primario' | 'secundario' | 'texto';
  cargando?: boolean;
  deshabilitado?: boolean;
  submit?: boolean;
}) {
  return (
    <button
      type={props.submit ? 'submit' : 'button'}
      className={`btn btn--${props.tipo ?? 'primario'}`}
      onClick={props.onClick}
      disabled={props.deshabilitado || props.cargando}
      aria-busy={props.cargando || undefined}
    >
      {props.cargando && <span className="spinner" aria-hidden />}
      {props.children}
    </button>
  );
}

export function Alerta(props: { tipo: 'error' | 'aviso' | 'info' | 'exito'; titulo?: string; children?: ReactNode }) {
  return (
    <div className={`alerta alerta--${props.tipo}`} role={props.tipo === 'error' ? 'alert' : 'status'}>
      {props.titulo && <strong>{props.titulo}</strong>}
      {props.children && <div>{props.children}</div>}
    </div>
  );
}

export function ErrorApi({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    return (
      <Alerta tipo="error" titulo={error.titulo}>
        {error.detalles.length > 0 && (
          <ul>
            {error.detalles.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
        {error.codigoAlanube && <small>Código: {error.codigoAlanube}</small>}
      </Alerta>
    );
  }
  return <Alerta tipo="error" titulo="Ocurrió un error inesperado." />;
}

export function Pill(props: { tono: 'ok' | 'pendiente' | 'error' | 'neutro'; children: ReactNode }) {
  return <span className={`pill pill--${props.tono}`}>{props.children}</span>;
}

/**
 * Guía "Cómo hacerlo" al inicio de cada pantalla. Se puede ocultar; la preferencia se recuerda en este navegador
 * (si el almacenamiento no está disponible, simplemente se muestra abierta).
 */
export function Guia(props: { id: string; titulo: string; pasos: ReactNode[]; nota?: ReactNode }) {
  const clave = `pa-fe-guia-${props.id}`;
  const [abierta, setAbierta] = useState<boolean>(() => {
    try {
      return localStorage.getItem(clave) !== '0';
    } catch {
      return true;
    }
  });
  const alternar = () => {
    const v = !abierta;
    setAbierta(v);
    try {
      localStorage.setItem(clave, v ? '1' : '0');
    } catch {
      /* sin almacenamiento: no se recuerda */
    }
  };
  return (
    <aside className={'guia' + (abierta ? ' guia--abierta' : '')} aria-label={props.titulo}>
      <button type="button" className="guia__cab" onClick={alternar} aria-expanded={abierta}>
        <span className="guia__icono" aria-hidden>?</span>
        <span className="guia__titulo">{props.titulo}</span>
        <span className="guia__toggle">{abierta ? 'Ocultar' : 'Mostrar'}</span>
      </button>
      {abierta && (
        <div className="guia__cuerpo">
          <ol>
            {props.pasos.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ol>
          {props.nota && <p className="guia__nota">{props.nota}</p>}
        </div>
      )}
    </aside>
  );
}
